import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Search } from "lucide-react";

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function daysOverdue(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  const today = new Date();
  return Math.floor((today - d) / (1000 * 60 * 60 * 24));
}

export default function OverdueDispatches() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/dispatches/overdue").then(({ data }) => setItems(data)); }, []);

  const query = q.trim().toLowerCase();
  const filtered = query
    ? items.filter((d) => [d.dispatch_code, d.invoice_number, d.lr_number, d.client_po_number, d.expected_delivery, d.delivery_status]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(query)))
    : items;

  return (
    <div data-testid="page-overdue" className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Alerts</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={26} /> Delivery Alerts
          </h1>
          <p className="text-sm text-slate-500 mt-1">Dispatches that have crossed their expected delivery date and are not yet marked as delivered.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-slate-500">Overdue</div>
          <div className="font-heading text-4xl font-extrabold text-rose-700 mt-1">{filtered.length}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 max-w-xl">
        <Search size={14} className="text-slate-400" />
        <input
          data-testid="input-overdue-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search dispatch code, invoice, LR, PO, status…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Dispatch</th><th className="px-5 py-3">Invoice</th>
              <th className="px-5 py-3">LR</th><th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Expected</th><th className="px-5 py-3">Days Late</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => {
              const late = daysOverdue(d.expected_delivery);
              return (
                <tr key={d.id} data-testid={`overdue-row-${d.id}`} className="table-row border-t border-slate-100">
                  <td className="px-5 py-3 font-mono text-xs">{d.dispatch_code}</td>
                  <td className="px-5 py-3">{d.invoice_number}</td>
                  <td className="px-5 py-3 font-mono text-xs">{d.lr_number}</td>
                  <td className="px-5 py-3">{fmtInr(d.invoice_amount)}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{d.expected_delivery}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${late > 7 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                      {late} day{late === 1 ? "" : "s"}
                    </span>
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={d.delivery_status} /></td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={`btn-followup-${d.id}`} onClick={() => nav(`/dispatches/${d.id}`)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold">Follow up →</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-slate-500 text-sm">{q ? `No matches for "${q}"` : "All caught up. No overdue dispatches."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
